import { createReducer, on } from '@ngrx/store';
import { EquipmentActions } from './equipment.actions';
import { ACMRemote, ACMRemoteAssignment, DiscoveredACMRemote, Equipment, EquipmentConfiguration, SignalRegister, EquipmentConnectionsResponse } from '../../../shared/interfaces/equipment.interfaces';
import { Firmware, FirmwareVersion } from '../../../shared/interfaces/firmware.interfaces';

export const equipmentFeatureKey = 'equipment';

export interface EquipmentState {
  count?: number | undefined;
  equipments?: Equipment[] | undefined;
  filter?: string | undefined;
  pageIndex?: number | undefined;
  sort?: {
    sortField: string | undefined;
    sortOrder: 1 | -1 | undefined;
  }
  foundEquipment?: Equipment | undefined;
  searchError?: any | undefined;
  selectedEquipment?: Equipment | any | undefined;
  signalHistory?: SignalRegister[] | undefined;
  connectionHistory?: EquipmentConnectionsResponse | undefined;
  hardwareList?: string[] | undefined;
  firmwareList?: Firmware[] | undefined;
  versionList?: FirmwareVersion[] | undefined;
  allEquipmentsForStats?: Equipment[] | undefined;
  configuration?: EquipmentConfiguration | undefined;
  configurationUtt?: any | undefined;
  pendingUpgrade?: string | undefined;
  dealerACMRemotes?: ACMRemote[] | undefined;
  discoveredACMRemotes?: DiscoveredACMRemote[] | undefined
}

export const initialState: EquipmentState = {
};

export const equipmentReducer = createReducer(
  initialState,

  on(EquipmentActions.loadEquipmentsSuccess, (state, action) => { return { ...state, count: action.data.count, equipments: action.data.equipments, }; }),

  on(EquipmentActions.setFilter, (state, action) => { return { ...state, filter: action.filter }; }),
  on(EquipmentActions.setPageIndex, (state, action) => ({ ...state, pageIndex: action.pageIndex })),
  on(EquipmentActions.setSort, (state, action) => ({ ...state, sort: action.sort })),

  on(EquipmentActions.loadEquipmentByIdSuccess, (state, action) => { return { ...state, selectedEquipment: action.data }; }),

  on(EquipmentActions.loadEquipmentSignalHistorySuccess, (state, action) => { return { ...state, signalHistory: action.data }; }),
  on(EquipmentActions.loadEquipmentConnectionHistorySuccess, (state, action) => ({ ...state, connectionHistory: action.data })),
  on(EquipmentActions.clearEquipmentConnectionHistory, (state) => ({ ...state, connectionHistory: undefined })),

  on(EquipmentActions.unsetSelectedEquipment, (state) => { return { ...state, selectedEquipment: undefined, signalHistory: undefined, hardwareList: undefined, firmwareList: undefined, versionList: undefined, configuration: undefined, configurationUtt: undefined, pendingUpgrade: undefined, connectionHistory: undefined }; }),

  on(EquipmentActions.updateEquipmentNameSuccess, (state, action) => {
    if (!state.selectedEquipment) return state;
    return {
      ...state,
      selectedEquipment: { ...state.selectedEquipment, name: action.data.name }
    };
  }),

  on(EquipmentActions.updateEquipmentNameOnListSuccess, (state, action) => {
    if (!state.equipments) return state;
    const updatedEquipments = state.equipments.map(eq =>
      eq.id === action.data.id ? { ...eq, name: action.data.name } : eq
    );
    return { ...state, equipments: updatedEquipments };
  }),

  on(EquipmentActions.updateEquipmentSimcardSuccess, (state, action) => {
    if (!state.selectedEquipment) return state;
    return {
      ...state,
      selectedEquipment: {
        ...state.selectedEquipment,
        simcard: { ...action.data }
      }
    };
  }),

  on(EquipmentActions.loadHardwareListSuccess, (state, action) => { return { ...state, hardwareList: action.data }; }),

  on(EquipmentActions.loadFirmwareListSuccess, (state, action) => { return { ...state, firmwareList: action.data.firmwareNames }; }),

  on(EquipmentActions.loadVersionListSuccess, (state, action) => { return { ...state, versionList: action.data.firmwareVersions }; }),

  on(EquipmentActions.readPendingFirmwareUpgradeSuccess, (state, action) => {
    let pendingUpgrade = action.data.pendingUpgrade;
    if (typeof pendingUpgrade === 'string' && pendingUpgrade.includes(':')) {
      // Extract the part after the last colon
      const lastColon = pendingUpgrade.lastIndexOf(':');
      if (lastColon !== -1) {
        const afterColon = pendingUpgrade.substring(lastColon + 1);
        // Always get the 2nd and 3rd values after the port (ntlinkg01E001,0008.04.01)
        const parts = afterColon.split(',');
        if (parts.length >= 3) {
          pendingUpgrade = parts[1] + '-' + parts[2];
          if (parts.length > 3) {
            // Append any additional parts separated by hyphens
            for (let i = 3; i < parts.length; i++) {
              pendingUpgrade += '-' + parts[i];
            }
          }
        } else {
          pendingUpgrade = afterColon;
        }
      }
    }
    return { ...state, pendingUpgrade };
  }),

  on(EquipmentActions.clearPendingUpgrade, (state) => { return { ...state, pendingUpgrade: undefined, connectionHistory: undefined }; }),

  on(EquipmentActions.searchEquipmentSuccess, (state, action) => { return { ...state, foundEquipment: action.data, searchError: undefined }; }),
  on(EquipmentActions.searchEquipmentFailure, (state, action) => { return { ...state, foundEquipment: undefined, searchError: action.error }; }),

  on(EquipmentActions.claimEquipmentSuccess, (state, action) => { return { ...state, foundEquipment: undefined, selectedEquipment: action.data[0] }; }),

  on(EquipmentActions.unsetFoundEquipment, (state) => { return { ...state, foundEquipment: undefined, searchError: undefined }; }),

  on(EquipmentActions.unsetEquipmentStore, () => initialState),

  on(EquipmentActions.loadEquipmentStatsSuccess, (state, action) => { return { ...state, allEquipmentsForStats: action.data }; }),

  on(EquipmentActions.updateEquipmentPanel, (state, action) => {
    if (!state.selectedEquipment) return state;
    return {
      ...state, selectedEquipment: { ...state.selectedEquipment, panel: action.panel }
    };
  }),

  on(EquipmentActions.loadEquipmentConfigurationSuccess, (state, action) => { return { ...state, configuration: action.data, configurationUtt: action.configurationUtt }; }),

  on(EquipmentActions.loadACMRemotesSuccess, (state, action) => { return { ...state, dealerACMRemotes: action.data }; }),

  on(EquipmentActions.loadACMRemoteByIdSuccess, (state, action) => { return { ...state, selectedACMRemote: action.data }; }),

  on(EquipmentActions.loadACMRemotesByAccountIdSuccess, (state, action) => { return { ...state, accountACMRemotes: action.data }; }),

  on(EquipmentActions.addDiscoveredACMRemote, (state, action) => {
    return {
      ...state,
      discoveredACMRemotes: [...(state.discoveredACMRemotes || []), action.data]
    };
  }),

  on(EquipmentActions.unsetDiscoveredRemotes, (state) => { return { ...state, discoveredACMRemotes: undefined }; }),

);
